<template>
  <div class="pagination-pages">
    <MIcon
      class="pagination-pages__item pagination-pages__arrow-icon"
      :class="{'pagination-pages__arrow-icon_disabled': isPrevDisabled}"
      :icon="bulletArrowLeftIcon"
      @click="changeToPrevPage"
    />
    <template v-for="(page, index) in pages">
      <a
        v-if="page.number"
        :key="`page-${page.number}`"
        class="pagination-pages__item pagination-pages__page-number"
        :class="{'pagination-pages__page-number_active': page.active}"
        @click="changePage(page.number)"
      > {{ page.number }} </a>
      <span
        v-else
        :key="`page-ellipses-${index}`"
        class="pagination-pages__item pagination-pages__ellipses"
      >...</span>
    </template>
    <MIcon
      class="pagination-pages__item pagination-pages__arrow-icon"
      :class="{'pagination-pages__arrow-icon_disabled': isNextDisabled}"
      :icon="bulletArrowRightIcon"
      @click="changeToNextPage"
    />
  </div>
</template>

<script>
  import bulletArrowRightIcon from 'assets/icons/bullet-arrow-right.svg';
  import bulletArrowLeftIcon from 'assets/icons/bullet-arrow-left.svg';
  import MIcon from 'components/Design/MIcon';

  export default {
    name: 'PaginationPages',
    components: {MIcon},
    props: {
      currentPage: {
        type: Number,
        required: true
      },
      firstPage: {
        type: Number,
        required: true
      },
      lastPage: {
        type: Number,
        required: true
      },
      threshold: {
        type: Number,
        default: 1
      }
    },
    data () {
      return {
        bulletArrowLeftIcon,
        bulletArrowRightIcon
      };
    },
    computed: {
      pages () {
        const pagesShown = [];
        const self = this;

        /*
         * to show first page, last page, current page, page after current,
         * sometimes page after first or before last page and '...' between them
         * examples from guideline (`` for current page):
         * `1` 2 ... 15 16
         * `1` 2 3 4
         *  1 ... `7` 8 ... 16
         */
        [...Array(self.lastPage - self.firstPage + 1).keys()].map((n) => n + 1).map((number) => {
          if (number <= self.firstPage || number >= self.lastPage ||
            number >= self.currentPage && number <= self.currentPage + self.threshold ||
            self.currentPage >= self.lastPage - self.threshold && number <= self.firstPage + self.threshold ||
            self.currentPage >= self.lastPage - self.threshold && number >= self.lastPage - self.threshold ||
            self.currentPage === self.firstPage && number >= self.lastPage - self.threshold ||
            self.lastPage - self.firstPage === 2 * self.threshold + 2) {
            pagesShown.push({number, active: self.isPageCurrent(number)});
          } else if (pagesShown[pagesShown.length - 1].number !== null) {
            pagesShown.push({number: null});
          }
          return {number, active: self.isPageCurrent(number)};
        });
        return pagesShown;
      },
      isPrevDisabled () {
        return this.currentPage === this.firstPage;
      },
      isNextDisabled () {
        return this.currentPage === this.lastPage;
      }
    },
    methods: {
      isPageCurrent (page) {
        return page === this.currentPage;
      },
      changePage (number) {
        if (this.isPageCurrent(number)) {
          return;
        }
        this.$emit('input', number);
      },
      changeToPrevPage () {
        if (this.isPrevDisabled) {
          return;
        }
        this.changePage(this.currentPage - 1);
      },
      changeToNextPage () {
        if (this.isNextDisabled) {
          return;
        }
        this.changePage(this.currentPage + 1);
      }
    }
  };
</script>

<style>
  .pagination-pages {
    --color-active-page: #15385C;

    font-size: 14px;
    font-weight: var(--medium-font-weight);

    color: var(--action-color);

    position: absolute;
    display: flex;

    justify-content: left;
    align-items: center;
  }

  .pagination-pages__item {
    cursor: pointer;

    padding-left: 5px;
    padding-right: 5px;
    user-select: none;
  }

  .pagination-pages__page-number {
    &.pagination-pages__page-number_active {
      color: var(--color-active-page);
    }

    &.pagination-pages__page-number_active:hover {
      color: var(--color-active-page);
      cursor: default;
    }
  }

  .pagination-pages__arrow-icon {
    width: 5px;
    height: 28px;

    &:hover {
      cursor: pointer;
    }

    &.pagination-pages__arrow-icon_disabled {
      color: var(--action-disabled-color);
    }

    &.pagination-pages__arrow-icon_disabled:hover {
      cursor: not-allowed;
    }
  }

  @media (--extra-small), (--small) {
    .pagination-pages {
      position: inherit;
      margin-right: auto;
    }
  }

  @media (--extra-small) {
    .pagination-pages {
      order: -1;
      margin-right: initial;
    }
  }
</style>
